import { Button } from '@amore/ui/components/button'
import { Field } from '@amore/ui/components/field'
import { FileInput } from '@amore/ui/components/file-input'
import { FormLayout } from '@amore/ui/components/form-layout'
import { Download, Trash2, Upload } from 'lucide-react'
import { useRef, useState } from 'react'
import { newId } from '@/lib/format'
import { usePrototype } from '@/mock/store'
import type { CeremonyDocument } from '@/mock/types'
import { ConfirmDialog, DetailSheet } from '@/patterns/dialog'
import { useToast } from '@/shell/toaster'
import { CeremonyTitleField, editorSections } from './editor-fields'
import { downloadCeremonyFile } from './file-actions'
import { CeremonyFilePreview } from './file-preview'

export function CeremonyDocumentSheet({
  item,
  onClose,
}: {
  item?: CeremonyDocument
  onClose: () => void
}) {
  const { canEdit, update } = usePrototype()
  const { showToast } = useToast()
  const [file, setFile] = useState(item?.file)
  const [name, setName] = useState(item?.name ?? '')
  const [error, setError] = useState<string>()
  const [fileError, setFileError] = useState<string>()
  const [confirmDelete, setConfirmDelete] = useState(false)
  const titleRef = useRef<HTMLTextAreaElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const form = useRef<HTMLFormElement>(null)
  const dirty = canEdit && (file !== item?.file || name !== (item?.name ?? ''))
  const remove = () => {
    if (!canEdit || !item) return
    update((current) => ({
      ...current,
      ceremony: {
        ...current.ceremony,
        documents: current.ceremony.documents.filter((record) => record.id !== item.id),
      },
    }))
    showToast({ title: 'Soubor odebrán', description: item.name })
    onClose()
  }
  return (
    <>
      <DetailSheet
        open
        eyebrow="Podklady"
        title={item?.name || 'Nový soubor'}
        titleContent={
          <CeremonyTitleField
            ref={titleRef}
            label="Název souboru"
            value={name}
            onChange={(value) => {
              setName(value)
              setError(undefined)
            }}
            readOnly={!canEdit}
            error={error}
          />
        }
        dirty={dirty}
        onOpenChange={(open) => {
          if (!open) onClose()
        }}
        primaryAction={
          canEdit ? { label: 'Uložit', onClick: () => form.current?.requestSubmit() } : undefined
        }
        destructiveAction={
          canEdit && item
            ? {
                label: 'Odebrat soubor',
                icon: <Trash2 className="size-4" aria-hidden="true" />,
                onClick: () => setConfirmDelete(true),
              }
            : undefined
        }
      >
        <FormLayout
          ref={form}
          noValidate
          className={editorSections}
          onSubmit={(event) => {
            event.preventDefault()
            if (!canEdit) return
            if (!file) {
              setFileError('Vyberte soubor.')
              fileRef.current?.focus()
              return
            }
            if (!name.trim() || name.trim().length > 150) {
              setError('Doplňte název souboru, nejvýše 150 znaků.')
              titleRef.current?.focus()
              return
            }
            const saved: CeremonyDocument = {
              id: item?.id ?? newId('ceremony-file'),
              name: name.trim(),
              sizeBytes: file.size,
              file,
            }
            update((current) => ({
              ...current,
              ceremony: {
                ...current.ceremony,
                documents: item
                  ? current.ceremony.documents.map((record) =>
                      record.id === item.id ? saved : record,
                    )
                  : [...current.ceremony.documents, saved],
              },
            }))
            showToast({
              title: item ? 'Soubor uložen' : 'Soubor přidán',
              description: saved.name,
              tone: 'success',
            })
            onClose()
          }}
        >
          <section className="space-y-4">
            {file ? (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs text-muted-foreground">
                  {file.name.includes('.')
                    ? file.name.split('.').at(-1)?.toLocaleUpperCase('cs')
                    : 'Soubor'}{' '}
                  · {Math.max(1, Math.round(file.size / 1000))} kB
                </p>
                <Button
                  appearance="outline"
                  tone="neutral"
                  size="sm"
                  onClick={() => downloadCeremonyFile(file, name.trim() || file.name)}
                >
                  <Download className="size-3.5" aria-hidden="true" />
                  Stáhnout
                </Button>
              </div>
            ) : null}
            {canEdit ? (
              <Field controlId="ceremony-document-file" error={fileError}>
                <FileInput
                  ref={fileRef}
                  data-autofocus={(!item && canEdit) || undefined}
                  aria-label={file ? 'Nahradit soubor' : 'Vybrat soubor'}
                  onChange={(event) => {
                    const next = event.target.files?.[0]
                    if (!next) return
                    setFile(next)
                    setName(next.name)
                    setError(undefined)
                    setFileError(undefined)
                  }}
                >
                  <Upload className="size-4" aria-hidden="true" />
                  {file ? 'Nahradit soubor' : 'Vybrat soubor'}
                </FileInput>
              </Field>
            ) : null}
          </section>
          {file ? (
            <section className="space-y-3">
              <h3 className="text-sm font-semibold">Náhled</h3>
              <CeremonyFilePreview
                key={`${file.name}-${file.lastModified}-${file.size}`}
                file={file}
                name={name || file.name}
              />
            </section>
          ) : null}
        </FormLayout>
      </DetailSheet>
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        heading="Odebrat soubor?"
        description={`Soubor „${item?.name ?? ''}“ se odebere z podkladů k obřadu.`}
        keepLabel="Ponechat soubor"
        confirmLabel="Odebrat soubor"
        onConfirm={remove}
      />
    </>
  )
}
